import React, { useContext, useState } from "react";
import Avatar from "@mui/material/Avatar";
import Popover from "@mui/material/Popover";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import {
  MessageStatus,
  MessageTimestamp,
  useChannelStateContext,
  useChatContext,
  useMessageContext,
} from "stream-chat-react";
import { ChannelReadReceiptsContext } from "./ChannelReadReceiptsProvider";
import { getMessageViewers, getViewerName } from "./messageReadReceiptUtils";

// Who may see read receipts on messages they didn't send (managers and up in
// groups/channels). Provided by ChatDisplay from the user's permission role.
export const ReadReceiptsPolicyContext = React.createContext({
  canSeeOthersReceipts: () => false,
});

const isOneOnOneDirectMessage = (channel) =>
  channel?.type === "messaging" &&
  Object.keys(channel?.state?.members || {}).length === 2;

const MessageReadReceipts = () => {
  const [anchorElement, setAnchorElement] = useState(null);
  const { client } = useChatContext();
  const { channel } = useChannelStateContext();
  const { message } = useMessageContext();
  const receipts = useContext(ChannelReadReceiptsContext);
  const readBy = Object.values(receipts.read).filter((receipt) =>
    new Date(receipt.last_read) >= new Date(message?.created_at)
  ).map((receipt) => receipt.user);
  const viewers = getMessageViewers(readBy, client?.userID, message?.user?.id, {
    readState: receipts.read,
    members: receipts.members,
    messageCreatedAt: message?.created_at,
  });

  if (message?.parent_id) return null;
  if (receipts.status !== "ready") {
    return <button type="button" className="rightwork-message-viewers-button"
      disabled={receipts.status === "loading"} onClick={receipts.retry}>
      {receipts.status === "loading" ? "Loading views…" : "Views unavailable · Retry"}
    </button>;
  }

  if (!viewers.length) {
    return null;
  }

  // 1:1: only one possible reader, so a double check (Stream's read mark)
  // says it all; the viewer list is for groups and channels
  if (isOneOnOneDirectMessage(channel)) {
    return (
      <span className="rightwork-message-read-check" title="Read">
        <DoneAllIcon fontSize="inherit" />
      </span>
    );
  }

  const viewerCount = viewers.length;
  const viewerLabel = `Viewed by ${viewerCount} ${
    viewerCount === 1 ? "person" : "people"
  }`;
  const popoverOpen = Boolean(anchorElement);

  return (
    <>
      <button
        type="button"
        className="rightwork-message-viewers-button"
        aria-label={`${viewerLabel}. Show names.`}
        aria-haspopup="dialog"
        aria-expanded={popoverOpen}
        onClick={(event) => {
          event.stopPropagation();
          setAnchorElement(event.currentTarget);
        }}
        title={`${viewerLabel}. Click to show names.`}
      >
        <VisibilityOutlinedIcon aria-hidden="true" fontSize="inherit" />
        <span>{viewerCount}</span>
      </button>
      <Popover
        open={popoverOpen}
        anchorEl={anchorElement}
        onClose={() => setAnchorElement(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <div
          className="w-72 max-w-[calc(100vw-2rem)]"
          role="dialog"
          aria-label={viewerLabel}
        >
          <div className="border-b border-slate-200 px-4 py-3">
            <p className="font-semibold text-slate-900">Viewed by</p>
            <p className="text-xs text-slate-500">
              {viewerCount} {viewerCount === 1 ? "person" : "people"}
            </p>
          </div>
          <div className="max-h-80 overflow-y-auto py-1">
            {viewers.map((viewer) => {
              const viewerName = getViewerName(viewer);

              return (
                <div
                  key={viewer.id}
                  className="flex items-center gap-x-3 px-4 py-2"
                >
                  <Avatar
                    alt={viewerName}
                    src={viewer.image}
                    sx={{ bgcolor: "#6d28d9", height: 32, width: 32 }}
                  >
                    {viewerName[0]?.toUpperCase()}
                  </Avatar>
                  <p className="min-w-0 truncate text-sm text-slate-800">
                    {viewerName}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </Popover>
    </>
  );
};

export const MessageStatusWithViewers = (props) => (
  <MessageStatus {...props} MessageReadStatus={MessageReadReceipts} />
);

// Stream only renders MessageStatus on your own messages. For others'
// messages, managers get the same viewer control next to the timestamp.
export const MessageTimestampWithViewers = (props) => {
  const { isMyMessage, message } = useMessageContext();
  const { channel } = useChannelStateContext();
  const { canSeeOthersReceipts } = useContext(ReadReceiptsPolicyContext);
  const showOthersReceipts =
    canSeeOthersReceipts?.(channel) &&
    !isMyMessage?.() &&
    message?.type !== "deleted" &&
    !isOneOnOneDirectMessage(channel);

  return (
    <>
      <MessageTimestamp {...props} />
      {showOthersReceipts && <MessageReadReceipts />}
    </>
  );
};

export default MessageReadReceipts;
